import moment from "moment-timezone/builds/moment-timezone-with-data-10-year-range.js";

import { numberLocale, t } from "@/localization";
import {
  MUX_REPORT_EVENTS_FROM,
  MUX_REPORT_UNTIL,
  OWN_REPORT_FROM,
} from "./config";

// CDN viewer-hours on screen (Event Analytics, Profile › Viewer-hours). The
// numbers come from the hourly rollup (eventlive-cdk/lambda/viewerHours.ts):
// per event and UTC day, live and replay, plus the per-minute devices of live
// days. Every event includes 5,000 viewer-hours; ticketed events have no
// limit (eventlive.pro/help).

export const FAIR_USE_HOURS = 5000;
// Our CDN logs start on this day: viewing before it isn't counted.
export const COUNTED_FROM = "2026-08-19";
// Two live minutes further apart than this start a new chart.
const SESSION_GAP_MINUTES = 60;
// A chart starts and ends where at least this share of the peak watched: after
// a stream ends, a few players keep fetching it for hours, which would squeeze
// the stream itself into a corner. Those minutes still count as viewer-hours.
const EDGE_SHARE = 0.02;
// A stretch whose peak is below this share of the biggest one (or a single
// device) gets no chart of its own: someone opening the stream hours later.
const MINOR_SHARE = 0.1;
const MINUTE_MS = 60000;

// 0 · <0.1 · 3.4 · 271 · 5,101
export const formatHours = (hours) => {
  const h = Number(hours) || 0;
  if (h <= 0) return "0";
  if (h < 0.05) return `<${(0.1).toLocaleString(numberLocale())}`;
  if (h < 10) return (Math.round(h * 10) / 10).toLocaleString(numberLocale());
  return Math.round(h).toLocaleString(numberLocale());
};

export const formatCount = (n) =>
  (Number(n) || 0).toLocaleString(numberLocale());

// The 5,000 included viewer-hours of an event without tickets; null when the
// event has no limit (it sells tickets).
export const fairUse = (total) => {
  const limit = total?.fair_use_hours;
  if (!limit) return null;
  const used = Number(total.hours) || 0;
  return {
    limit,
    used,
    share: Math.min(1, used / limit),
    over: !!total.over_fair_use || used > limit,
    left: Math.max(0, limit - used),
  };
};

const dayStart = (day) => Date.parse(`${day}T00:00:00Z`);

// The live days' per-minute devices as charts: one per stretch of live
// viewing, joined across midnight UTC (an evening in the Americas is two UTC
// days), without the thin minutes before and after (EDGE_SHARE) or minor
// stretches (MINOR_SHARE).
// [{ start (ms), values: [devices per minute], peak, peakAt (ms) }]
export const liveSessions = (days = []) => {
  const minutes = new Map();
  for (const d of days || []) {
    if (d?.kind !== "live" || !Array.isArray(d.minute_devices)) continue;
    const base = dayStart(d.day) + (Number(d.minute_start) || 0) * MINUTE_MS;
    d.minute_devices.forEach((n, i) => {
      if (n > 0) {
        const at = base + i * MINUTE_MS;
        minutes.set(at, (minutes.get(at) || 0) + n);
      }
    });
  }
  const times = Array.from(minutes.keys()).sort((a, b) => a - b);
  const sessions = [];
  let current = null;
  for (const at of times) {
    if (!current || at - current.last > SESSION_GAP_MINUTES * MINUTE_MS) {
      current = { start: at, last: at, points: [] };
      sessions.push(current);
    }
    current.points.push([at, minutes.get(at)]);
    current.last = at;
  }
  const charts = sessions.map(({ start, last, points }) => {
    const all = new Array((last - start) / MINUTE_MS + 1).fill(0);
    let peak = 0;
    let peakAt = start;
    for (const [at, n] of points) {
      all[(at - start) / MINUTE_MS] = n;
      if (n > peak) {
        peak = n;
        peakAt = at;
      }
    }
    const edge = Math.max(1, Math.ceil(peak * EDGE_SHARE));
    const first = all.findIndex((n) => n >= edge);
    let end = all.length - 1;
    while (all[end] < edge) end--;
    return {
      start: start + first * MINUTE_MS,
      values: all.slice(first, end + 1),
      peak,
      peakAt,
    };
  });
  const top = charts.reduce((a, c) => (c.peak > a.peak ? c : a), charts[0]);
  return charts.filter(
    (c) => c === top || c.peak >= Math.max(2, top.peak * MINOR_SHARE)
  );
};

// The stretches the report charts: all of them up to `max`, else the `max`
// biggest (the peak's always among them), in time order.
export const chartSessions = (sessions = [], max = 3) =>
  sessions.length <= max
    ? sessions
    : sessions
        .slice()
        .sort((a, b) => b.peak - a.peak)
        .slice(0, max)
        .sort((a, b) => a.start - b.start);

// The chart's y-axis: 0 and round steps (1, 2 or 5 × 10ⁿ) up to the first
// one at or above the peak. Viewer counts are whole, so steps are too.
export const yTicks = (peak) => {
  if (!(peak > 0)) return [0, 1];
  const rough = peak / 3;
  const magnitude = Math.pow(10, Math.floor(Math.log10(rough)));
  const step = Math.max(
    1,
    [1, 2, 5, 10].map((m) => m * magnitude).find((s) => s >= rough)
  );
  const ticks = [0];
  while (ticks[ticks.length - 1] < peak) {
    ticks.push(ticks[ticks.length - 1] + step);
  }
  return ticks;
};

// One row per UTC day with hours: live and replay, newest first. (A day can
// have only playlist fetches or bots: no hours, no row.)
export const dayRows = (days = []) => {
  const byDay = new Map();
  for (const d of days || []) {
    if (!d?.day) continue;
    const row = byDay.get(d.day) || { day: d.day, live: 0, replay: 0 };
    if (d.kind === "live" || d.kind === "replay") {
      row[d.kind] += Number(d.hours) || 0;
    }
    byDay.set(d.day, row);
  }
  return Array.from(byDay.values())
    .filter((row) => row.live + row.replay > 0)
    .sort((a, b) => (a.day < b.day ? 1 : a.day > b.day ? -1 : 0));
};

// A breakdown ([{ name, devices, hours, views }] from the CDN, or Mux's
// [{ name, viewers, views, hours }]) as rows by one metric, with its value and
// share, largest first; "other" (everything past the top 15) last.
export const breakdownRows = (
  list = [],
  metric = "devices",
  top = Infinity
) => {
  const rows = (list || [])
    .filter((r) => r && (Number(r[metric]) || 0) > 0)
    .map((r) => ({ ...r, value: Number(r[metric]) || 0 }));
  const total = rows.reduce((a, r) => a + r.value, 0);
  const named = rows
    .filter((r) => r.name !== "other")
    .sort((a, b) => b.value - a.value || (a.name < b.name ? -1 : 1));
  // Past the first `top` rows, the rest join "other", so the rows still add up.
  const rest = [...named.slice(top), ...rows.filter((r) => r.name === "other")];
  const sum = (k) => rest.reduce((a, r) => a + (Number(r[k]) || 0), 0);
  const other = rest.length
    ? [
        {
          name: "other",
          devices: sum("devices"),
          viewers: sum("viewers"),
          hours: sum("hours"),
          views: sum("views"),
          value: sum("value"),
        },
      ]
    : [];
  return [...named.slice(0, top), ...other].map((r) => ({
    ...r,
    share: total ? r.value / total : 0,
  }));
};

// Which report Event Analytics shows for an event: "own", or "mux" while Mux
// still runs (config.js). Until the switch every event keeps Mux's report;
// after it, events dated before the switch keep it until MUX_REPORT_UNTIL, so
// the numbers of an event don't change while people are still watching it.
// Events without a date, or older than Mux's report, get ours.
export const reportSource = (
  event,
  now = Date.now(),
  { from = OWN_REPORT_FROM, until = MUX_REPORT_UNTIL } = {}
) => {
  if (now >= Date.parse(until)) return "own";
  const date = Date.parse(event?.event_datetime_utc || "");
  if (!Number.isFinite(date) || date < Date.parse(MUX_REPORT_EVENTS_FROM)) {
    return "own";
  }
  return now < Date.parse(from) || date < Date.parse(from) ? "mux" : "own";
};

// Whether our numbers are on screen yet (Profile › Viewer-hours).
export const ownReportStarted = (now = Date.now(), from = OWN_REPORT_FROM) =>
  now >= Date.parse(from);

// Whether the event started before the CDN counts did, so part of its
// viewing isn't in the numbers.
export const startedBeforeCounts = (event) =>
  !!event?.event_datetime_utc &&
  event.event_datetime_utc.slice(0, 10) < COUNTED_FROM;

// "2026-10" → a moment for the month's first day (the app's language decides
// the month name).
export const monthMoment = (month) => moment.utc(`${month}-01`, "YYYY-MM-DD");

// When the numbers last changed, on the viewer's clock with its time zone
// ("Nov 3, 2026", "11:05 AM GMT"), for "Last updated {date}, {time}".
export const lastUpdated = (iso, zone = moment.tz.guess()) => {
  const at = moment.tz(iso, zone && moment.tz.zone(zone) ? zone : "UTC");
  return { date: at.format(t("MMM D, YYYY")), time: `${at.format(t("h:mm A"))} ${at.format("z")}` };
};

// A time in the event's time zone (or the device's when it has none).
export const atEventTime = (ms, timezone) =>
  timezone && moment.tz.zone(timezone) ? moment(ms).tz(timezone) : moment(ms);
